<template>
  <div id="create-reservation-modal" class="modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <a data-dismiss="modal" href="javascript:;">
          <XIcon class="w-8 h-8 text-gray-500" />
        </a>
        <h2 class="text-lg font-medium p-5">{{ $t('reservation.add') }}</h2>
        <form @submit.prevent="handleSave">
          <div class="modal-body p-0">
            <div class="p-5 text-left">
              <div class="mt-3">
                <label
                  for="daterange"
                  class="form-label w-full flex flex-col sm:flex-row"
                >
                  {{ $t('common.date') }}
                </label>
                <div class="relative w-56 mx-auto">
                  <div
                    class="absolute rounded-l w-10 h-full flex items-center justify-center bg-gray-100 border text-gray-600 dark:bg-dark-1 dark:border-dark-4"
                  >
                    <CalendarIcon class="w-4 h-4" />
                  </div>
                  <Litepicker
                    v-model="daterange"
                    :options="{
                      autoApply: false,
                      singleMode: false,
                      numberOfColumns: 2,
                      numberOfMonths: 2,
                      minDate: dateConstraint,
                      dropdowns: {
                        minYear: 2023,
                        maxYear: null,
                        months: true,
                        years: true
                      },
                      lang: 'sk-SK'
                    }"
                    class="form-control pl-12 w-56 block mx-auto"
                  />
                </div>
                <template v-if="validate.daterange.$error">
                  <div
                    v-for="(error, index) in validate.daterange.$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </div>
              <div class="mt-3">
                <label for="timeFrom" class="form-label w-full flex flex-col sm:flex-row">
                  {{ $t('form.field.timeFrom') }}
                </label>
                <input
                  id="timeFrom"
                  v-model.trim="validate.timeFrom.$model"
                  type="time"
                  name="timeFrom"
                  class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                  :class="{
                    'border-theme-6': validate.timeFrom.$error
                  }"
                  placeholder="08:00"
                />
                <template v-if="validate.timeFrom.$error">
                  <div
                    v-for="(error, index) in validate.timeFrom.$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </div>
              <div class="mt-3">
                <label for="timeTo" class="form-label w-full flex flex-col sm:flex-row">
                  {{ $t('form.field.timeTo') }}
                </label>
                <input
                  id="timeTo"
                  v-model.trim="validate.timeTo.$model"
                  type="time"
                  name="timeTo"
                  class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                  :class="{ 'border-theme-6': validate.timeTo.$error }"
                  placeholder="08:00"
                />
                <template v-if="validate.timeTo.$error">
                  <div
                    v-for="(error, index) in validate.timeTo.$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </div>
              <div class="mt-3">
                <BranchSelect v-model:selectedBranch="selectedBranch" />
              </div>
              <div class="mt-3">
                <ServiceSelect v-model:selectedService="selectedService" />
              </div>
              <div class="mt-3">
                <EmployeeSelect v-model:selectedEmployee="selectedEmployee" />
              </div>
              <div class="mt-3">
                <ResourceSelect v-model:selectedResource="selectedResource" />
              </div>
              <div class="mt-3">
                <BranchPlaceSelect v-model:selectedBranchPlace="selectedBranchPlace" />
              </div>
              <div class="mt-3">
                <PaymentTypeSelect v-model:payment="payment" />
              </div>
              <div class="form-check flex-col items-start mt-3">
                <label for="blockByProvider" class="form-check-label ml-0 mb-2">
                  {{ $t('common.blockByProvider') }} - 
                  <span class="sm:ml-auto mt-1 sm:mt-0 ml-10 text-theme-6">
                    {{ $t('common.blockByProviderDescription') }}
                  </span>
                </label>
                <input
                  id="blockByProvider"
                  v-model="validate.blockByProvider.$model"
                  name="blockByProvider"
                  class="form-check-switch"
                  type="checkbox"
                />
              </div>
              <div v-if="!validate.blockByProvider.$model">
                <div class="mt-3">
                  <label for="name" class="form-label w-full flex flex-col sm:flex-row">
                    {{ $t('common.personName') }}
                  </label>
                  <input
                    id="name"
                    v-model.trim="validate.name.$model"
                    type="text"
                    name="name"
                    class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                    :class="{ 'border-theme-6': validate.name.$error }"
                    placeholder="John"
                  />
                  <template v-if="validate.name.$error">
                    <div
                      v-for="(error, index) in validate.name.$errors"
                      :key="index"
                      class="text-theme-6 mt-2"
                    >
                      {{ $t(error.$message) }}
                    </div>
                  </template>
                </div>
                <div class="mt-3">
                  <label
                    for="lastname"
                    class="form-label w-full flex flex-col sm:flex-row"
                  >
                    {{ $t('common.personLastname') }}
                  </label>
                  <input
                    id="lastname"
                    v-model.trim="validate.lastname.$model"
                    type="text"
                    name="lastname"
                    class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                    :class="{ 'border-theme-6': validate.lastname.$error }"
                    placeholder="Doe"
                  />
                  <template v-if="validate.lastname.$error">
                    <div
                      v-for="(error, index) in validate.lastname.$errors"
                      :key="index"
                      class="text-theme-6 mt-2"
                    >
                      {{ $t(error.$message) }}
                    </div>
                  </template>
                </div>
                <div class="mt-3">
                  <label
                    for="phoneNumber"
                    class="form-label w-full flex flex-col sm:flex-row"
                  >
                    {{ $t('common.phoneNumber') }}
                  </label>
                  <input
                    id="phoneNumber"
                    v-model.trim="validate.phoneNumber.$model"
                    type="text"
                    name="phoneNumber"
                    class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                    :class="{ 'border-theme-6': validate.phoneNumber.$error }"
                    placeholder="+421xxxyyyzzz"
                  />
                  <template v-if="validate.phoneNumber.$error">
                    <div
                      v-for="(error, index) in validate.phoneNumber.$errors"
                      :key="index"
                      class="text-theme-6 mt-2"
                    >
                      {{ $t(error.$message) }}
                    </div>
                  </template>
                </div>
                <div class="mt-3">
                  <label for="email" class="form-label w-full flex flex-col sm:flex-row">
                    Email
                  </label>
                  <input
                    id="email"
                    v-model.trim="validate.email.$model"
                    type="text"
                    name="email"
                    class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                    :class="{ 'border-theme-6': validate.email.$error }"
                    placeholder="info@info.info"
                  />
                  <template v-if="validate.email.$error">
                    <div
                      v-for="(error, index) in validate.email.$errors"
                      :key="index"
                      class="text-theme-6 mt-2"
                    >
                      {{ $t(error.$message) }}
                    </div>
                  </template>
                </div>
                <div class="mt-3">
                  <label for="note" class="form-label w-full flex flex-col sm:flex-row">
                    {{ $t('common.note') }}
                  </label>
                  <textarea
                    id="note"
                    name="note"
                    class="intro-y form-control py-3 px-4 box pr-10 shadow-md border"
                    v-model="note"
                  >
                  </textarea>
                </div>
              </div>
              <div class="mt-3">
                <div
                  class="col-span-6 sm:col-span-3 xl:col-span-2 flex flex-col justify-end items-center"
                  v-if="savingReservation"
                >
                  <LoadingIcon icon="three-dots" class="w-8 h-8" />
                </div>
                <button
                  type="submit"
                  class="btn btn-primary shadow-md flex items-center"
                  v-if="!savingReservation"
                >
                  <SaveIcon class="w-4 h-4 mr-2" />
                  {{ $t('reservation.create') }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- BEGIN: Success Notification Content -->
  <div id="success-notification-create-reservation" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('reservation.created') }}!</div>
      <!-- <div class="text-gray-600 mt-1">
        Please check your e-mail for further info!
      </div> -->
    </div>
  </div>
  <!-- END: Success Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-notification-create-reservation" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('reservation.notCreated') }}!</div>
      <div class="text-gray-600 mt-1" ref="failedNotificationCreateReservationText"></div>
    </div>
  </div>
  <!-- END: Failed Notification Content -->
</template>

<script>
import { defineComponent, ref, reactive, toRefs, toRaw } from 'vue'

import { required, helpers, email, requiredIf } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import BranchSelect from '../../components/branches/BranchSelect.vue'
import BranchPlaceSelect from '../../components/branches/BranchPlaceSelect.vue'
import EmployeeSelect from '../../components/employees/EmployeeSelect.vue'
import ResourceSelect from '../../components/resources/ResourceSelect.vue'
import ServiceSelect from '../../components/services/ServiceSelect.vue'
import PaymentTypeSelect from '../../components/payments/PaymentTypeSelect.vue'
import Toastify from 'toastify-js'
import reservationsApi from '../../services/reservations/reservations.api.js'

export default defineComponent({
  components: {
    BranchSelect,
    BranchPlaceSelect,
    EmployeeSelect,
    ResourceSelect,
    ServiceSelect,
    PaymentTypeSelect
  },
  data() {
    return {
      state: 0
    }
  },
  methods: {
    setError(error, callback) {
      this.$refs.failedNotificationCreateReservationText.textContent = this.$t(error)
      callback()
    },
    checkEmpty(data) {
      if (data === null || data === undefined || data === '') {
        return null
      }
      return data
    },
    handleSave(data) {
      this.savingReservation = true

      this.validate.$touch()
      if (this.validate.$invalid) {
        this.setError('form.validation.invalidForm', () =>
          Toastify({
            node: cash('#failed-notification-create-reservation')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
        )
        this.savingReservation = false
        return
      }

      const dateRegex = /^(\d+)\.(\d+)\.(\d+) - (\d+)\.(\d+)\.(\d+)$/

      if (this.formData.blockByProvider) {
        this.formData.name = this.$t('common.blockedByProvider')
      }
      const saveRequest = {
        branchUUID: toRaw(this.selectedBranch.selectedBranch) ?? null,
        branchPlaceUUID: toRaw(this.selectedBranchPlace.selectedBranchPlace) ?? null,
        customer: {
          name: this.formData.name ?? null,
          lastname: this.formData.lastname ?? null,
          phoneNumber: this.formData.phoneNumber ?? null,
          mail: this.formData.email ?? null
        },
        dateFrom: this.daterange.replace(dateRegex, '$3-$2-$1'),
        dateTo: this.daterange.replace(dateRegex, '$6-$5-$4'),
        employeeUUID: toRaw(this.selectedEmployee.selectedEmployee) ?? null,
        note: this.note,
        resourceUUID: toRaw(this.selectedResource.selectedResource) ?? null,
        serviceUUID: toRaw(this.selectedService.selectedService) ?? null,
        timeFrom: this.formData.timeFrom,
        timeTo: this.formData.timeTo,
        paymentType: toRaw(this.payment.payment)
      }

      saveRequest.branchPlaceUUID = this.validateUuid(saveRequest.branchPlaceUUID)
      saveRequest.employeeUUID = this.validateUuid(saveRequest.employeeUUID)
      saveRequest.resourceUUID = this.validateUuid(saveRequest.resourceUUID)

      reservationsApi.create(saveRequest).then(
        (response) => {
          Toastify({
            node: cash('#success-notification-create-reservation')
              .clone()
              .removeClass('hidden')[0],
            duration: 5000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.savingReservation = false
          this.hideComponent()
          this.reloadCalendar()
        },
        (error) => {
          this.setError('error.' + error.response.data.error.message_key, () =>
            Toastify({
              node: cash('#failed-notification-create-reservation')
                .clone()
                .removeClass('hidden')[0],
              duration: 10000,
              newWindow: true,
              close: true,
              gravity: 'top',
              position: 'right',
              stopOnFocus: true
            }).showToast()
          )
          this.savingReservation = false
        }
      )
    },
    validateUuid(uuid) {
      if (uuid === null) {
        return null
      }
      return uuid.match(/^([0-9a-z-]+)$/i) ? uuid : null
    },
    displayComponent() {
      const d = new Date()
      const date = d.getDate() + '.' + (d.getMonth() + 1) + '.' + d.getFullYear()
      d.setMinutes(0)
      d.setHours(d.getHours() + 1)
      const d2 = new Date()
      d2.setMinutes(0)
      d2.setHours(d2.getHours() + 2)
      this.daterange = date + ' - ' + date
      this.savingReservation = false
      this.state = 0
      this.formData.timeFrom = d.toLocaleTimeString().substring(0, 5)
      this.formData.timeTo = d2.toLocaleTimeString().substring(0, 5)
      this.formData.daterange = this.daterange
      this.formData.name = ''
      this.formData.lastname = ''
      this.formData.phoneNumber = ''
      this.formData.email = ''
      this.formData.blockByProvider = false // Reset checkbox on modal open
      this.validate.$reset() // Reset validation state

      cash('#create-reservation-modal').modal('show')
    },
    hideComponent() {
      this.savingReservation = false
      this.state = 0
      cash('#create-reservation-modal').modal('hide')
    }
  },
  props: {
    reloadCalendar: Function
  },
  setup() {
    const savingReservation = ref(false)
    // phoneOrEmail ref is no longer needed
    const d = new Date()
    const date = d.getDate() + '.' + d.getMonth() + '.' + d.getFullYear()
    const daterange = ref(date + ' - ' + date)
    const note = ref('')
    const selectedBranch = ref({})
    const selectedBranchPlace = ref({})
    const selectedEmployee = ref({})
    const selectedResource = ref({})
    const selectedService = ref({})
    const payment = ref({})

    const dateConstraint = new Date() - 1

    const timeValidator = helpers.regex(/^([0-1][0-9]|2[0-4]):[0-5][0-9]$/)
    const daterangeValidator = helpers.regex(
      /^(\d+)\.(\d+)\.(\d+) - (\d+)\.(\d+)\.(\d+)$/
    )
    const phoneNumberValidator = helpers.regex(/^\+421[0-9]{9}$/)
    d.setMinutes(0)
    d.setHours(d.getHours() + 1)
    const d2 = new Date()
    d2.setMinutes(0)
    d2.setHours(d2.getHours() + 2)
    const formData = reactive({
      timeFrom: d.toLocaleTimeString().substring(0, 5),
      timeTo: d2.toLocaleTimeString().substring(0, 5),
      daterange: daterange,
      name: '',
      lastname: '',
      phoneNumber: '',
      email: '',
      blockByProvider: false
    })

    // --- ZMENY SÚ V TEJTO ČASTI ---
    const rules = {
      timeFrom: {
        required: helpers.withMessage('form.validation.requiredField', required),
        timeValidator
      },
      timeTo: {
        required: helpers.withMessage('form.validation.requiredField', required),
        timeValidator
      },
      daterange: {
        required: helpers.withMessage('form.validation.requiredField', required),
        daterangeValidator
      },
      name: {
        // Pole je povinné, iba ak `blockByProvider` NIE JE zaškrtnuté
        required: helpers.withMessage(
          'form.validation.requiredField',
          requiredIf(() => !formData.blockByProvider)
        )
      },
      lastname: {
        // Pole je povinné, iba ak `blockByProvider` NIE JE zaškrtnuté
        required: helpers.withMessage(
          'form.validation.requiredField',
          requiredIf(() => !formData.blockByProvider)
        )
      },
      phoneNumber: {
        phoneNumberValidator: helpers.withMessage(
          'form.validation.phoneNumberValidator',
          // Validátor formátu sa použije len ak nie je `blockByProvider` a pole má nejakú hodnotu
          (value) => {
            if (formData.blockByProvider || !value) {
              return true
            }
            return phoneNumberValidator(value)
          }
        ),
        // Pole je povinné, iba ak nie je `blockByProvider` A ZÁROVEŇ je pole `email` prázdne
        phoneOrEmail: helpers.withMessage(
          'form.validation.phoneOrEmail',
          requiredIf(() => !formData.blockByProvider && !formData.email)
        )
      },
      email: {
        email: helpers.withMessage(
          'form.validation.email',
          // Validátor formátu sa použije len ak nie je `blockByProvider` a pole má nejakú hodnotu
          (value) => {
            if (formData.blockByProvider || !value) {
              return true
            }
            return email(value) // `email` je importovaný validátor
          }
        ),
        // Pole je povinné, iba ak nie je `blockByProvider` A ZÁROVEŇ je pole `phoneNumber` prázdne
        phoneOrEmail: helpers.withMessage(
          'form.validation.phoneOrEmail',
          requiredIf(() => !formData.blockByProvider && !formData.phoneNumber)
        )
      },
      blockByProvider: {}
    }
    // --- KONIEC ZMIEN ---

    const validate = useVuelidate(rules, toRefs(formData))

    return {
      savingReservation,
      dateConstraint,
      daterange,
      formData,
      note,
      selectedBranch,
      selectedBranchPlace,
      selectedEmployee,
      selectedResource,
      selectedService,
      payment,
      validate
      // phoneOrEmail sa už nevracia
    }
  }
})
</script>
